import {
  Button,
  Dialog,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import moment from "moment";
import { FormattedNumber } from "react-intl";
import { ReimbursementRequest, ReimbursementRequestStatus } from "services/GraphQl";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PageviewOutlinedIcon from "@mui/icons-material/PageviewOutlined";
import { getDownloadURL, ref } from "firebase/storage";
import { useNotifications } from "contexts/Notification";
import { useFirebase } from "contexts/Firebase";

const ViewReimbursementModal = ({
  openViewReimbursement,
  setOpenViewReimbursement,
  reimbursement,
}: {
  openViewReimbursement: boolean;
  setOpenViewReimbursement: Function;
  reimbursement: ReimbursementRequest;
}) => {
  const { storage } = useFirebase();
  const { notifications } = useNotifications();

  const viewattachment = (index: number) => {
    const file = reimbursement.files[index];
    const fileRef = ref(storage, file.path);
    getDownloadURL(fileRef)
      .then((downloadURL) => {
        window.open(downloadURL, "_blank");
      })
      .catch((error) => {
        console.error("Error getting download URL:", error);
        notifications.error(`File is not compatible with your browser so it has been downloaded.`);
        downloadAttachement(file);
      });
  };

  const downloadAttachement = async (file: any) => {
    try {
      const storageRef = ref(storage, file.path);
      const url = await getDownloadURL(storageRef);
      const xhr = new XMLHttpRequest();
      xhr.responseType = "blob";
      xhr.onload = () => {
        const blob = xhr.response;
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      };
      xhr.open("GET", url);
      xhr.send();
      notifications.success(`${file.name} has been downloaded successfully.`);
    } catch (error) {
      console.error("Error downloading file:");
    }
  };

  return (
    <Dialog
      open={openViewReimbursement}
      onClose={() => {
        setOpenViewReimbursement(false);
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          marginX: 4,
          marginTop: 2,
        }}
      >
        <Typography variant="h2" sx={{ pb: 2, fontSize: { xs: "1.2rem", sm: "1.4rem", ms: "1.8rem" }, width: "85%" }}>
          Reimbursement Details
        </Typography>
        <IconButton
          sx={{ position: "absolute", right: "30px", color: "primary", width: "15%" }}
          onClick={() => {
            setOpenViewReimbursement(false);
          }}
        >
          <CloseIcon />
        </IconButton>
        <Divider sx={{ backgroundColor: "brightOrange.main" }}></Divider>
      </Box>
      <Box
        sx={{
          p: 2,
        }}
      >
        <Box sx={{ paddingX: 2, paddingY: 0.5 }}>
          <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
            <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}> Request Date:</Typography>
            <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
              {reimbursement?.createdAt ? moment(reimbursement.createdAt).format("MM/DD/YYYY") : "--"}
            </Typography>
          </Box>
          <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
            <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}>Amount Requested:</Typography>
            <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
              <FormattedNumber
                style={`currency`}
                maximumFractionDigits={2}
                currency="usd"
                value={reimbursement?.amount}
              />
            </Typography>
          </Box>
          <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
            <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}>Amount Approved:</Typography>
            <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
              {reimbursement.approvedAmount ? (
                <FormattedNumber
                  style={`currency`}
                  maximumFractionDigits={2}
                  currency="usd"
                  value={reimbursement?.approvedAmount}
                />
              ) : (
                <>--</>
              )}
            </Typography>
          </Box>
          <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
            <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}>Coverage Months:</Typography>
            <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
              {reimbursement?.coverageMonths ? (
                <>{reimbursement.coverageMonths.map((month) => moment(month).format("MM/YYYY")).join(", ")}</>
              ) : null}
            </Typography>
          </Box>
          <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
            <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}>Status:</Typography>
            <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
              {reimbursement?.status}
            </Typography>
          </Box>
          {reimbursement.status === ReimbursementRequestStatus.Denied && (
            <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", flexDirection: "row" }}>
              <Typography sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" } }}>Denial Reason:</Typography>
              <Typography sx={{ paddingX: 2, fontSize: { xs: "0.85rem", sm: "0.9rem", ms: "1rem" }, fontWeight: 600 }}>
                {reimbursement?.notes}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ margin: 2 }}>
          <Typography variant="h3" sx={{ fontSize: { xs: "1rem", sm: "1.2rem", ms: "1.5rem" } }}>
            Attachments:{" "}
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ border: "none" }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: { xs: "0.7rem", sm: "0.8rem", ms: "0.875rem" },
                      p: { xs: 1, sm: 1, ms: 2 },
                    },
                  }}
                >
                  <TableCell>File Name</TableCell>
                  <TableCell>View</TableCell>
                  <TableCell>Download</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reimbursement.files &&
                  reimbursement.files.map((file, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "& .MuiTableCell-root": {
                          fontSize: { xs: "0.7rem", sm: "0.8rem", ms: "0.875rem" },
                          p: { xs: 1, sm: 1, ms: 2 },
                        },
                      }}
                    >
                      <TableCell>{file?.name}</TableCell>
                      <TableCell>
                        <Tooltip
                          title="View Attachment"
                          arrow
                          onClick={() => {
                            viewattachment(index);
                          }}
                        >
                          <IconButton color="primary">
                            <PageviewOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title="Download Attachment"
                          arrow
                          onClick={() => {
                            downloadAttachement(file);
                          }}
                        >
                          <IconButton color="primary">
                            <DownloadOutlinedIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ margin: { xs: 1, ms: 2 }, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              width: "100%",
              marginTop: 3,
              marginBottom: 2,
              padding: { xs: 0.5, ms: 1, sm: 1.5 },
              fontSize: { xs: "0.7rem", sm: "0.8rem", ms: "0.875rem" },
            }}
            color="secondary"
            onClick={() => {
              setOpenViewReimbursement(false);
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
export default ViewReimbursementModal;
